import { useEffect, useMemo, useRef, useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import type { Database } from "@/integrations/supabase/types";
import {
  AlertTriangle,
  Check,
  ImageIcon,
  Loader2,
  MapPin,
  Plus,
  Save,
  Trash2,
  Upload,
  X,
} from "lucide-react";
import { MarkdownEditor, type MarkdownEditorSnippet } from "@/components/MarkdownEditor.lazy";
import { useCaseCoverUrl, useCaseCoverUrls } from "@/lib/use-cases";
import { CASE_TAGS, getCaseTagMeta, type CaseTag } from "@/lib/cases/tags";
import { resolvePostalCode } from "@/lib/geo/postal-code.functions";
import { parseCaseSegments, extractCaseImagePaths, parseVimeoRef } from "@/lib/cases/parseModules";
import { SegmentRenderer } from "@/components/cases/blocks/CaseBlocks";
import { confirmDialog } from "@/lib/confirm-dialog";
import { AdminListToolbar } from "@/components/admin/AdminListToolbar";
import {
  AudioUploadDialog,
  CaseMediaLibrary,
  VimeoPreviewPanel,
} from "@/components/admin/cases/CaseMediaTools";
import { retryImageOnce } from "@/lib/storage-image";


// Ticket AV6: Autorenfelder und geplante Veröffentlichung werden hier nicht mehr
// gelesen oder geschrieben (bei 61 Datensätzen praktisch leer).
type CaseRow = Omit<
  Database["public"]["Tables"]["cases"]["Row"],
  "author" | "author_image_path" | "author_profile_id" | "scheduled_for"
>;
type CaseInsert = Database["public"]["Tables"]["cases"]["Insert"];


export const Route = createFileRoute("/_admin/admin/cases")({
  component: AdminCasesPage,
});

const EMPTY_DRAFT: CaseInsert = {
  slug: "",
  title: "",
  client_name: null,
  excerpt: "",
  content_md: "",
  cover_image_path: null,
  tags: [],
  postal_code: null,
  city: null,
  latitude: null,
  longitude: null,
  published: false,
  priority: "medium",
  // Testimonial (optional) — Spalten via Migration hinzugefügt
  testimonial_quote: null,
  testimonial_author: null,
  testimonial_role: null,
  testimonial_image_path: null,
} as CaseInsert;

type CasePriority = Database["public"]["Enums"]["case_priority"];

const CASE_PRIORITY_LABELS: Record<CasePriority, string> = {
  high: "Hoch",
  medium: "Mittel",
  low: "Tief",
};

const CASE_PRIORITY_BADGE_CLASS: Record<CasePriority, string> = {
  high: "bg-primary/15 text-primary",
  medium: "bg-secondary text-foreground",
  low: "bg-muted text-muted-foreground",
};

const dateFmt = new Intl.DateTimeFormat("de-DE", {
  day: "2-digit",
  month: "2-digit",
  year: "numeric",
});

function toLocalInputValue(iso: string | null | undefined): string {
  if (!iso) return "";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return "";
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}
function fromLocalInputValue(v: string): string | null {
  if (!v) return null;
  const d = new Date(v);
  return isNaN(d.getTime()) ? null : d.toISOString();
}

function slugifyLive(s: string) {
  return s
    .toLowerCase()
    .replace(/ä/g, "ae")
    .replace(/ö/g, "oe")
    .replace(/ü/g, "ue")
    .replace(/ß/g, "ss")
    .replace(/[^a-z0-9-]+/g, "-");
}

function slugify(s: string) {
  return slugifyLive(s).replace(/(^-|-$)/g, "");
}

const TAG_LABEL = new Map(CASE_TAGS.map((t) => [t.value, t.label]));

function AdminCasesPage() {
  const [items, setItems] = useState<CaseRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState<CaseRow | null>(null);
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [tagFilter, setTagFilter] = useState<string | null>(null);
  const queryClient = useQueryClient();

  const tagCounts = useMemo(() => {
    const m = new Map<string, number>();
    for (const c of items) for (const t of c.tags ?? []) m.set(t, (m.get(t) ?? 0) + 1);
    return [...m.entries()].sort((a, b) =>
      (TAG_LABEL.get(a[0] as CaseTag) ?? a[0]).localeCompare(TAG_LABEL.get(b[0] as CaseTag) ?? b[0], "de"),
    );
  }, [items]);

  const filteredItems = useMemo(
    () => (tagFilter ? items.filter((c) => (c.tags ?? []).includes(tagFilter as CaseTag)) : items),
    [items, tagFilter],
  );

  // Stufe 2: Freitextsuche zusätzlich zum Tag-Filter.
  const [query, setQuery] = useState("");
  const visibleItems = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return filteredItems;
    return filteredItems.filter((c) =>
      [c.title, c.slug, c.city, c.postal_code, c.client_name]
        .filter(Boolean)
        .join(" ")
        .toLowerCase()
        .includes(q),
    );
  }, [filteredItems, query]);

  function invalidatePublicCaches() {
    queryClient.invalidateQueries({ queryKey: ["public", "cases"] });
    queryClient.invalidateQueries({ queryKey: ["public", "case"] });
  }

  async function load() {
    setLoading(true);
    // Ohne `content_md`: der Cases-Text wird erst beim Bearbeiten geladen.
    const { data, error } = await supabase
      .from("cases")
      .select(
        "id, slug, title, excerpt, reading_time, cover_image_path, tags, published, published_at, created_at, updated_at, postal_code, city, latitude, longitude, testimonial_quote, testimonial_author, testimonial_role, testimonial_image_path, client_name, priority",
      )
      .order("priority", { ascending: true })
      .order("published_at", { ascending: false, nullsFirst: false })
      .order("created_at", { ascending: false });
    if (error) setError(error.message);
    else setItems((data ?? []).map((row) => ({ ...row, content_md: "" })));
    setLoading(false);
  }

  // Case-Text erst beim Öffnen nachladen. Das Formular öffnet erst, wenn der
  // Text da ist — es übernimmt seine Startwerte nur einmal, sonst bliebe der
  // Text leer und ein Speichern würde ihn überschreiben.
  async function openCase(item: CaseRow) {
    setCreating(false);
    setEditing(null);
    const { data, error } = await supabase
      .from("cases")
      .select("content_md")
      .eq("id", item.id)
      .maybeSingle();
    if (error || !data) {
      setError(error?.message ?? "Case konnte nicht geladen werden.");
      return;
    }
    setEditing({ ...item, content_md: data.content_md ?? "" });
  }


  useEffect(() => {
    load();
  }, []);

  async function onDelete(item: CaseRow) {
    if (
      !(await confirmDialog({
        title: `Case „${item.title}" löschen?`,
        description: "Der Case und sein Titelbild werden dauerhaft entfernt.",
      }))
    )
      return;
    const { error } = await supabase.from("cases").delete().eq("id", item.id);
    if (error) {
      alert(error.message);
      return;
    }
    // Ticket AD3: Titelbild mitentfernen, sonst bleibt die Datei als Waise liegen.
    if (item.cover_image_path) {
      await supabase.storage.from("case-covers").remove([item.cover_image_path]);
    }
    invalidatePublicCaches();
    load();
  }

  return (
    <div>
      <header className="flex items-center justify-between">
        <div>
          <h1 className="font-display text-3xl font-extrabold md:text-4xl">Cases</h1>
          <p className="mt-2 max-w-2xl text-sm text-muted-foreground">
            Case Studies pflegen. Über die Tags werden die Cases den passenden
            Produktseiten zugeordnet. Über die Postleitzahl werden die Cases
            später auf einer Karte verortet.
          </p>
        </div>
        <button
          onClick={() => {
            setEditing(null);
            setCreating(true);
          }}
          className="inline-flex items-center gap-2 rounded-md bg-brand-button px-4 py-2 text-sm font-semibold text-brand-button-foreground"
        >
          <Plus className="h-4 w-4" /> Neuer Case
        </button>
      </header>

      {error && (
        <div className="mt-6 rounded-md border border-destructive/30 bg-destructive/10 p-4 text-sm text-destructive">
          {error}
        </div>
      )}

      <AdminListToolbar
        search={query}
        onSearchChange={setQuery}
        searchPlaceholder="Nach Titel, Ort oder Kunde suchen …"
        searchLabel="Cases durchsuchen"
        count={visibleItems.length}
        total={items.length}
      />

      <div className="mt-4 overflow-hidden rounded-lg border border-border bg-background">
        {loading ? (
          <div className="flex items-center gap-2 p-6 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" /> Laden …
          </div>
        ) : items.length === 0 ? (
          <p className="p-6 text-sm text-muted-foreground">Noch keine Cases angelegt.</p>
        ) : (
          <>
            <div className="flex flex-wrap items-center gap-2 border-b border-border bg-secondary/40 p-3">
              <span className="mr-1 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                Filter:
              </span>
              <button
                type="button"
                onClick={() => setTagFilter(null)}
                className={
                  "rounded-full px-3 py-1 text-xs font-semibold transition " +
                  (tagFilter === null
                    ? "bg-primary text-primary-foreground"
                    : "bg-background text-foreground/80 hover:bg-accent/20 border border-border")
                }
              >
                Alle ({items.length})
              </button>
              {tagCounts.map(([tag, count]) => {
                const active = tagFilter === tag;
                return (
                  <button
                    key={tag}
                    type="button"
                    onClick={() => setTagFilter(active ? null : tag)}
                    className={
                      "rounded-full px-3 py-1 text-xs font-semibold transition " +
                      (active
                        ? "bg-primary text-primary-foreground"
                        : "bg-background text-foreground/80 hover:bg-accent/20 border border-border")
                    }
                  >
                    #{TAG_LABEL.get(tag as CaseTag) ?? tag} ({count})
                  </button>
                );
              })}
            </div>
            {visibleItems.length === 0 ? (
              <p className="p-6 text-sm text-muted-foreground">
                Keine Cases passen zu Suche und Filter.
              </p>
            ) : (
              <CasesList
                items={visibleItems}
                onEdit={(p) => {
                  void openCase(p);
                }}
                onDelete={onDelete}
              />
            )}
          </>
        )}
      </div>

      {(editing || creating) && (
        <CaseForm
          initial={editing ?? EMPTY_DRAFT}
          onClose={() => {
            setEditing(null);
            setCreating(false);
          }}
          onSaved={() => {
            setEditing(null);
            setCreating(false);
            invalidatePublicCaches();
            load();
          }}
        />
      )}
    </div>
  );
}

function CasesList({
  items,
  onEdit,
  onDelete,
}: {
  items: CaseRow[];
  onEdit: (p: CaseRow) => void;
  onDelete: (p: CaseRow) => void;
}) {
  const coverResults = useCaseCoverUrls(items.map((p) => p.cover_image_path));

  return (
    <ul className="divide-y divide-border">
      {items.map((p, i) => {
        const coverUrl = coverResults[i]?.data ?? null;
        const location = [p.city, p.postal_code].filter(Boolean).join(" · ");
        return (
          <li key={p.id} className="flex items-start gap-4 p-4">
            <div className="flex aspect-[16/9] w-32 shrink-0 items-center justify-center overflow-hidden rounded-md border border-border bg-secondary">
              {coverUrl ? (
                <img
                  src={coverUrl}
                  onError={retryImageOnce}
                  alt=""
                  loading="lazy"
                  decoding="async"
                  className="h-full w-full object-cover"
                />
              ) : (
                <ImageIcon className="h-5 w-5 text-muted-foreground" />
              )}
            </div>
            <div className="flex-1">
              <p className="font-display text-base font-extrabold text-primary">
                {p.title || <span className="text-muted-foreground">(ohne Titel)</span>}
              </p>
              {p.client_name && (
                <p className="mt-0.5 font-body text-sm text-foreground/85">
                  {p.client_name}
                </p>
              )}
              <p className="mt-1 text-xs text-muted-foreground">/cases/{p.slug}</p>
              <p className="mt-2 line-clamp-2 text-sm text-foreground/80">{p.excerpt}</p>
              <div className="mt-2 flex flex-wrap items-center gap-2 text-xs">
                {(p.tags ?? []).map((t) => (
                  <span
                    key={t}
                    className="rounded bg-accent/20 px-2 py-0.5 text-foreground/80"
                  >
                    #{TAG_LABEL.get(t as CaseTag) ?? t}
                  </span>
                ))}
                {location && (
                  <span className="inline-flex items-center gap-1 text-muted-foreground">
                    <MapPin className="h-3 w-3" aria-hidden /> {location}
                  </span>
                )}
                <span className="text-muted-foreground">
                  {dateFmt.format(new Date(p.published_at ?? p.created_at))}
                </span>
                <span
                  className={
                    "rounded px-2 py-0.5 " +
                    (p.published
                      ? "bg-brand-wahlen/20 text-foreground"
                      : "bg-muted text-muted-foreground")
                  }
                >
                  {p.published ? "Veröffentlicht" : "Entwurf"}
                </span>
                <span className={"rounded px-2 py-0.5 font-medium " + CASE_PRIORITY_BADGE_CLASS[p.priority]}>
                  Priorität: {CASE_PRIORITY_LABELS[p.priority]}
                </span>
              </div>
            </div>
            <div className="flex shrink-0 gap-2">
              <button
                onClick={() => onEdit(p)}
                className="rounded border border-border px-3 py-1 text-xs font-semibold hover:bg-secondary"
              >
                Bearbeiten
              </button>
              <button
                onClick={() => onDelete(p)}
                aria-label="Löschen"
                className="rounded border border-destructive/40 px-2 py-1 text-destructive hover:bg-destructive/10"
              >
                <Trash2 className="h-3.5 w-3.5" />
              </button>
            </div>
          </li>
        );
      })}
    </ul>
  );
}

function CaseForm({
  initial,
  onClose,
  onSaved,
}: {
  initial: CaseRow | CaseInsert;
  onClose: () => void;
  onSaved: () => void;
}) {
  const isEdit = "id" in initial;
  const [draft, setDraft] = useState<CaseInsert>({ ...initial });
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [previewMediaUrls, setPreviewMediaUrls] = useState<Record<string, string>>({});
  const testimonialImageUrl = useCaseCoverUrl(draft.testimonial_image_path ?? null).data ?? null;
  const audioResolverRef = useRef<((v: string | null) => void) | null>(null);
  const [audioDialogOpen, setAudioDialogOpen] = useState(false);

  const openAudioDialog = () =>
    new Promise<string | null>((resolve) => {
      audioResolverRef.current = resolve;
      setAudioDialogOpen(true);
    });

  const closeAudioDialog = (result: string | null) => {
    audioResolverRef.current?.(result);
    audioResolverRef.current = null;
    setAudioDialogOpen(false);
  };

  const snippets = useMemo<MarkdownEditorSnippet[]>(
    () =>
      CASE_MODULE_SNIPPETS.map((s) =>
        s.label === "Audio-Player" ? { ...s, onRequest: openAudioDialog } : s,
      ),
    [],
  );


  useEffect(() => {
    const md = draft.content_md ?? "";
    const paths = extractCaseImagePaths(md);
    const missing = paths.filter((p) => !previewMediaUrls[p]);
    if (missing.length === 0) return;
    let cancelled = false;
    const t = setTimeout(async () => {
      const { data } = await supabase.storage
        .from("case-covers")
        .createSignedUrls(missing, 60 * 60);
      if (cancelled || !data) return;
      const next: Record<string, string> = {};
      data.forEach((d, i) => {
        if (d.signedUrl) next[missing[i]] = d.signedUrl;
      });
      if (Object.keys(next).length > 0) {
        setPreviewMediaUrls((prev) => ({ ...prev, ...next }));
      }
    }, 250);
    return () => {
      cancelled = true;
      clearTimeout(t);
    };
  }, [draft.content_md, previewMediaUrls]);

  function renderTestimonialPreview() {
    const quote = draft.testimonial_quote?.trim();
    const author = draft.testimonial_author?.trim();
    const role = draft.testimonial_role?.trim();
    if (!quote && !author) {
      return (
        <div className="not-prose my-12 rounded-lg border border-dashed border-border bg-secondary/30 p-6 text-sm text-muted-foreground">
          Testimonial-Platzhalter — Zitat und Autor:in in den Feldern oben pflegen.
        </div>
      );
    }
    return (
      <figure className="not-prose my-12 rounded-lg bg-secondary/40 p-8">
        {quote && (
          <blockquote className="text-lg leading-relaxed text-foreground">„{quote}"</blockquote>
        )}
        {(author || role || testimonialImageUrl) && (
          <figcaption className="mt-4 flex items-center gap-3 text-sm text-muted-foreground">
            {testimonialImageUrl && (
              <img
                src={testimonialImageUrl}
                alt=""
                className="h-10 w-10 rounded-full object-cover"
              />
            )}
            <span>
              {author}
              {author && role ? " — " : ""}
              {role}
            </span>
          </figcaption>
        )}
      </figure>
    );
  }


  async function save() {
    setSaving(true);
    setErr(null);
    try {
      if (!draft.title.trim()) throw new Error("Bitte einen Titel vergeben.");
      const slug = draft.slug?.trim() ? draft.slug : slugify(draft.title);
      const payload: CaseInsert = {
        ...draft,
        slug,
        tags: (draft.tags ?? []).filter((t): t is CaseTag => TAG_LABEL.has(t as CaseTag)),
        published_at:
          draft.published && !draft.published_at
            ? new Date().toISOString()
            : draft.published_at ?? null,
      };

      if (isEdit && "id" in initial) {
        const { error } = await supabase
          .from("cases")
          .update(payload)
          .eq("id", (initial as CaseRow).id);
        if (error) throw error;
      } else {
        const { error } = await supabase.from("cases").insert(payload);
        if (error) throw error;
      }
      onSaved();
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="fixed inset-0 z-40 flex items-start justify-center overflow-y-auto bg-foreground/40 p-4 md:p-10">
      <div className="w-full max-w-4xl rounded-lg border border-border bg-background p-6 shadow-xl">
        <div className="flex items-center justify-between">
          <h2 className="font-display text-xl font-extrabold">
            {isEdit ? "Case bearbeiten" : "Neuer Case"}
          </h2>
          <button
            onClick={onClose}
            aria-label="Schließen"
            className="rounded p-1 text-muted-foreground hover:bg-secondary"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="mt-6 space-y-4">
          <div className="grid gap-4 md:grid-cols-2">
            <Field label="Titel">
              <input
                value={draft.title}
                onChange={(e) =>
                  setDraft((d) => ({
                    ...d,
                    title: e.target.value,
                    slug: d.slug && isEdit ? d.slug : slugify(e.target.value),
                  }))
                }
                className="w-full rounded-md border border-input bg-background p-2 text-sm"
              />
            </Field>
            <Field label="Slug (URL)">
              <input
                value={draft.slug}
                onChange={(e) => setDraft({ ...draft, slug: slugifyLive(e.target.value) })}
                onBlur={(e) => setDraft({ ...draft, slug: slugify(e.target.value) })}
                className="w-full rounded-md border border-input bg-background p-2 font-mono text-sm"
              />
            </Field>
          </div>

          <Field label="Kundenname (optional)">
            <input
              value={draft.client_name ?? ""}
              onChange={(e) =>
                setDraft({ ...draft, client_name: e.target.value.slice(0, 120) || null })
              }
              maxLength={120}
              placeholder="z. B. Stadt Musterhausen"
              className="w-full rounded-md border border-input bg-background p-2 text-sm"
            />
            <p className="mt-1 text-xs text-muted-foreground">
              Wird unter dem Titel des Cases im Frontend angezeigt.
            </p>
          </Field>

          <Field label="Kurzbeschreibung">
            <textarea
              value={draft.excerpt ?? ""}
              onChange={(e) => setDraft({ ...draft, excerpt: e.target.value })}
              rows={2}
              className="w-full rounded-md border border-input bg-background p-3 text-sm"
            />
          </Field>

          <Field label="Tags (steuern, auf welchen Produktseiten der Case erscheint)">
            <TagPicker
              value={(draft.tags ?? []) as CaseTag[]}
              onChange={(tags) => setDraft({ ...draft, tags })}
            />
          </Field>

          <PostalCodeField
            plz={draft.postal_code ?? ""}
            city={draft.city ?? ""}
            latitude={draft.latitude ?? null}
            longitude={draft.longitude ?? null}
            onChange={(v) =>
              setDraft({
                ...draft,
                postal_code: v.plz,
                city: v.city,
                latitude: v.latitude,
                longitude: v.longitude,
              })
            }
          />

          <Field label="Titelbild">
            <CoverImageUpload
              value={draft.cover_image_path ?? null}
              onChange={(p) => setDraft({ ...draft, cover_image_path: p })}
            />
          </Field>

          <TestimonialFields draft={draft} setDraft={setDraft} />

          <CaseMediaLibrary
            onInsert={(md: string) =>
              setDraft((prev) => ({
                ...prev,
                content_md: (prev.content_md ?? "") + (prev.content_md?.endsWith("\n") ? "" : "\n\n") + md + "\n",
              }))
            }
          />

          <Field label="Inhalt (Markdown)">
            <MarkdownEditor
              value={draft.content_md ?? ""}
              onChange={(v) => setDraft({ ...draft, content_md: v })}
              extraSnippets={snippets}
              renderPreview={(v) => {
                const family = (draft.tags ?? [])
                  .map((t) => getCaseTagMeta(t))
                  .find((m): m is NonNullable<typeof m> => !!m)?.family;
                const statsVariant =
                  family === "wahlen-gewinnen"
                    ? "wahlen"
                    : family === "politisch-kommunizieren"
                      ? "kommunikation"
                      : "default";
                return (
                  <div className="mx-auto max-w-5xl markdown-article markdown-article--wide">
                    <SegmentRenderer
                      segments={parseCaseSegments(v)}
                      mediaUrls={previewMediaUrls}
                      renderTestimonial={renderTestimonialPreview}
                      statsVariant={statsVariant}
                    />
                  </div>
                );
              }}
            />

            <p className="mt-2 text-xs text-muted-foreground">
              Modul-Bausteine über die Buttons in der Editor-Leiste einfügen. Bilder oben unter „Case-Medien" hochladen und den Pfad in <code className="rounded bg-secondary px-1">src="…"</code> bzw. in die Galerie-Zeilen einsetzen.
            </p>
          </Field>

          <VimeoPreviewPanel content={draft.content_md ?? ""} />

          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={draft.published ?? false}
              onChange={(e) => setDraft({ ...draft, published: e.target.checked })}
            />
            Veröffentlichen (öffentlich sichtbar)
          </label>

          <Field label="Priorität">
            <select
              value={(draft.priority ?? "medium") as CasePriority}
              onChange={(e) =>
                setDraft({ ...draft, priority: e.target.value as CasePriority })
              }
              className="w-full rounded-md border border-input bg-background p-2 text-sm md:w-72"
            >
              <option value="high">Hoch</option>
              <option value="medium">Mittel</option>
              <option value="low">Tief</option>
            </select>
            <p className="mt-1 text-xs text-muted-foreground">
              Steuert die Reihenfolge in öffentlichen Case-Listen: „Hoch" zuerst, dann „Mittel", zuletzt „Tief".
            </p>
          </Field>

        </div>

        {err && (
          <p className="mt-4 rounded border border-destructive/30 bg-destructive/10 p-3 text-xs text-destructive">
            {err}
          </p>
        )}

        <div className="mt-6 flex justify-end gap-2">
          <button
            onClick={onClose}
            className="rounded-md border border-border px-4 py-2 text-sm font-semibold"
          >
            Abbrechen
          </button>
          <button
            onClick={save}
            disabled={saving}
            className="inline-flex items-center gap-2 rounded-md bg-brand-button px-4 py-2 text-sm font-semibold text-brand-button-foreground disabled:opacity-60"
          >
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
            Speichern
          </button>
        </div>
      </div>
      {audioDialogOpen && (
        <AudioUploadDialog
          onCancel={() => closeAudioDialog(null)}
          onDone={(md) => closeAudioDialog(md)}
        />
      )}
    </div>
  );
}


function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="block">
      <span className="mb-1 block text-xs font-semibold uppercase tracking-wide text-muted-foreground">
        {label}
      </span>
      {children}
    </label>
  );
}

function TagPicker({
  value,
  onChange,
}: {
  value: CaseTag[];
  onChange: (tags: CaseTag[]) => void;
}) {
  function toggle(tag: CaseTag) {
    if (value.includes(tag)) onChange(value.filter((t) => t !== tag));
    else onChange([...value, tag]);
  }
  return (
    <div className="flex flex-wrap gap-2 rounded-md border border-input bg-background p-3">
      {CASE_TAGS.map((t) => {
        const active = value.includes(t.value);
        return (
          <button
            key={t.value}
            type="button"
            onClick={() => toggle(t.value)}
            className={
              "rounded-full border px-3 py-1 text-xs font-semibold transition-colors " +
              (active
                ? "border-primary bg-primary text-primary-foreground"
                : "border-border bg-background text-foreground hover:bg-secondary")
            }
          >
            {t.label}
          </button>
        );
      })}
    </div>
  );
}

function normalizeCity(v: string): string {
  return v
    .trim()
    .toLowerCase()
    .replace(/ß/g, "ss")
    .replace(/ä/g, "a")
    .replace(/ö/g, "o")
    .replace(/ü/g, "u")
    .replace(/[\s\-–_/]+/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}

function citiesMatch(a: string, b: string): boolean {
  const na = normalizeCity(a);
  const nb = normalizeCity(b);
  if (!na || !nb) return false;
  if (na === nb) return true;
  // Tolerant: Ortsteil "Berlin-Mitte" vs. "Berlin"
  return na.startsWith(nb + " ") || nb.startsWith(na + " ");
}

function PostalCodeField({
  plz,
  city,
  latitude,
  longitude,
  onChange,
}: {
  plz: string;
  city: string;
  latitude: number | null;
  longitude: number | null;
  onChange: (v: {
    plz: string | null;
    city: string | null;
    latitude: number | null;
    longitude: number | null;
  }) => void;
}) {
  const resolveFn = useServerFn(resolvePostalCode);
  const [busy, setBusy] = useState(false);
  const [resolvedCity, setResolvedCity] = useState<string | null>(null);
  const [notFound, setNotFound] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [formatMsg, setFormatMsg] = useState<string | null>(null);

  async function lookup(value: string) {
    setError(null);
    setFormatMsg(null);
    if (!value) {
      setResolvedCity(null);
      setNotFound(false);
      onChange({ plz: null, city: null, latitude: null, longitude: null });
      return;
    }
    if (!/^[0-9]{5}$/.test(value)) {
      setFormatMsg("PLZ muss aus 5 Ziffern bestehen.");
      setResolvedCity(null);
      setNotFound(false);
      return;
    }
    setBusy(true);
    try {
      const res = await resolveFn({ data: { plz: value } });
      if (res.found && res.city) {
        setResolvedCity(res.city);
        setNotFound(false);
        // City nur automatisch befüllen, wenn noch leer — sonst manuellen Wert behalten.
        onChange({
          plz: value,
          city: city && city.trim() ? city : res.city,
          latitude: res.latitude,
          longitude: res.longitude,
        });
      } else {
        setResolvedCity(null);
        setNotFound(true);
        onChange({ plz: value, city: city || null, latitude: null, longitude: null });
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }

  const mismatch =
    !!resolvedCity && !!city && city.trim().length > 0 && !citiesMatch(city, resolvedCity);
  const matches =
    !!resolvedCity && !!city && city.trim().length > 0 && citiesMatch(city, resolvedCity);

  return (
    <div>
      <span className="mb-1 block text-xs font-semibold uppercase tracking-wide text-muted-foreground">
        Postleitzahl &amp; Ort
      </span>
      <div className="flex flex-wrap items-center gap-2">
        <input
          value={plz}
          inputMode="numeric"
          pattern="[0-9]*"
          maxLength={5}
          onChange={(e) => {
            const v = e.target.value.replace(/\D/g, "").slice(0, 5);
            onChange({ plz: v || null, city, latitude, longitude });
          }}
          onBlur={(e) => lookup(e.target.value)}
          placeholder="z. B. 10115"
          className="w-32 rounded-md border border-input bg-background p-2 font-mono text-sm"
          aria-label="Postleitzahl"
        />
        <input
          value={city}
          onChange={(e) =>
            onChange({ plz: plz || null, city: e.target.value || null, latitude, longitude })
          }
          placeholder="Ort"
          className="min-w-[12rem] flex-1 rounded-md border border-input bg-background p-2 text-sm"
          aria-label="Ort"
        />
        {busy && (
          <span className="inline-flex items-center gap-1.5 text-xs text-muted-foreground">
            <Loader2 className="h-3.5 w-3.5 animate-spin" /> prüfe …
          </span>
        )}
      </div>

      <div className="mt-1.5 min-h-[1.25rem] text-xs">
        {formatMsg && <p className="text-destructive">{formatMsg}</p>}
        {error && <p className="text-destructive">{error}</p>}
        {!formatMsg && !error && notFound && (
          <p className="text-muted-foreground">PLZ nicht gefunden. Speichern dennoch möglich.</p>
        )}
        {!formatMsg && !error && mismatch && resolvedCity && (
          <div className="flex flex-wrap items-center gap-2 rounded-md border border-brand-accent/40 bg-brand-accent/10 px-2 py-1.5 text-brand-accent">
            <AlertTriangle className="h-3.5 w-3.5 shrink-0" aria-hidden />
            <span>
              Laut PLZ {plz}: <strong>{resolvedCity}</strong> — eingetragen: „{city}".
            </span>
            <button
              type="button"
              onClick={() =>
                onChange({ plz: plz || null, city: resolvedCity, latitude, longitude })
              }
              className="ml-auto inline-flex items-center gap-1 rounded border border-brand-accent/50 bg-background px-2 py-0.5 text-[11px] font-semibold hover:bg-brand-accent hover:text-white"
            >
              Übernehmen
            </button>
          </div>
        )}
        {!formatMsg && !error && !mismatch && matches && (
          <p className="inline-flex items-center gap-1.5 text-brand-wahlen">
            <Check className="h-3.5 w-3.5" aria-hidden /> Ort passt zur PLZ
            {latitude != null && longitude != null && (
              <span className="text-muted-foreground">
                ({latitude.toFixed(3)}, {longitude.toFixed(3)})
              </span>
            )}
          </p>
        )}
        {!formatMsg && !error && !mismatch && !matches && !notFound && resolvedCity && !city && (
          <p className="inline-flex items-center gap-1.5 text-muted-foreground">
            <MapPin className="h-3.5 w-3.5" aria-hidden /> Vorschlag: {resolvedCity}
          </p>
        )}
        {!formatMsg && !error && !resolvedCity && !notFound && !busy && !plz && (
          <p className="text-muted-foreground">
            PLZ eingeben — Ort wird beim Verlassen des Felds aufgelöst.
          </p>
        )}
      </div>
    </div>
  );
}


function CoverImageUpload({
  value,
  onChange,
}: {
  value: string | null;
  onChange: (path: string | null) => void;
}) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const { data: url } = useCaseCoverUrl(value);

  async function handleFile(file: File) {
    setErr(null);
    setUploading(true);
    try {
      const ext = file.name.split(".").pop()?.toLowerCase() || "bin";
      const path = `cases/${crypto.randomUUID()}.${ext}`;
      const { error } = await supabase.storage
        .from("case-covers")
        .upload(path, file, {
          cacheControl: "31536000",
          contentType: file.type || undefined,
          upsert: false,
        });
      if (error) throw error;
      if (value) {
        await supabase.storage.from("case-covers").remove([value]);
      }
      onChange(path);
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      setUploading(false);
    }
  }

  async function clear() {
    if (value) {
      await supabase.storage.from("case-covers").remove([value]);
    }
    onChange(null);
  }

  return (
    <div className="space-y-3">
      <div className="flex items-start gap-4">
        <div className="flex aspect-[16/9] w-48 shrink-0 items-center justify-center overflow-hidden rounded-md border border-border bg-secondary">
          {url ? (
            <img src={url} alt="Titelbild" className="h-full w-full object-cover" />
          ) : (
            <ImageIcon className="h-6 w-6 text-muted-foreground" />
          )}
        </div>
        <div className="flex flex-col gap-2">
          <button
            type="button"
            onClick={() => inputRef.current?.click()}
            disabled={uploading}
            className="inline-flex items-center gap-2 rounded-md border border-border bg-background px-3 py-2 text-xs font-semibold hover:bg-secondary disabled:opacity-60"
          >
            {uploading ? (
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
            ) : (
              <Upload className="h-3.5 w-3.5" />
            )}
            {value ? "Bild ersetzen" : "Bild hochladen"}
          </button>
          {value && (
            <button
              type="button"
              onClick={clear}
              className="inline-flex items-center gap-2 rounded-md border border-destructive/40 px-3 py-2 text-xs font-semibold text-destructive hover:bg-destructive/10"
            >
              <Trash2 className="h-3.5 w-3.5" /> Entfernen
            </button>
          )}
          <p className="text-xs text-muted-foreground">
            JPG, PNG oder WebP. Empfohlen: 1600×900 px (16:9).
          </p>
        </div>
      </div>
      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        className="hidden"
        onChange={(e) => {
          const f = e.target.files?.[0];
          if (f) handleFile(f);
          e.target.value = "";
        }}
      />
      {err && <p className="text-xs text-destructive">{err}</p>}
    </div>
  );
}

function TestimonialFields({
  draft,
  setDraft,
}: {
  draft: CaseInsert;
  setDraft: (updater: (d: CaseInsert) => CaseInsert) => void;
}) {
  const d = draft as CaseInsert & {
    testimonial_quote?: string | null;
    testimonial_author?: string | null;
    testimonial_role?: string | null;
    testimonial_image_path?: string | null;
  };
  return (
    <fieldset className="space-y-4 rounded-md border border-border bg-secondary/30 p-4">
      <legend className="px-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
        Testimonial (optional)
      </legend>
      <p className="text-xs text-muted-foreground">
        Wird im Text an der Stelle <code className="rounded bg-background px-1">:::testimonial:::</code>{" "}
        eingefügt. Ohne Marker erscheint es nach der ersten Zwischenüberschrift.
      </p>
      <Field label="Zitat">
        <textarea
          value={d.testimonial_quote ?? ""}
          onChange={(e) =>
            setDraft((prev) => ({ ...prev, testimonial_quote: e.target.value || null } as CaseInsert))
          }
          rows={3}
          className="w-full rounded-md border border-input bg-background p-3 text-sm"
        />
      </Field>
      <div className="grid gap-4 md:grid-cols-2">
        <Field label="Name">
          <input
            value={d.testimonial_author ?? ""}
            onChange={(e) =>
              setDraft((prev) => ({ ...prev, testimonial_author: e.target.value || null } as CaseInsert))
            }
            className="w-full rounded-md border border-input bg-background p-2 text-sm"
          />
        </Field>
        <Field label="Rolle / Funktion">
          <input
            value={d.testimonial_role ?? ""}
            onChange={(e) =>
              setDraft((prev) => ({ ...prev, testimonial_role: e.target.value || null } as CaseInsert))
            }
            className="w-full rounded-md border border-input bg-background p-2 text-sm"
          />
        </Field>
      </div>
      <Field label="Bild">
        <TestimonialImageUpload
          value={d.testimonial_image_path ?? null}
          onChange={(p) =>
            setDraft((prev) => ({ ...prev, testimonial_image_path: p } as CaseInsert))
          }
        />
      </Field>
    </fieldset>
  );
}

function TestimonialImageUpload({
  value,
  onChange,
}: {
  value: string | null;
  onChange: (path: string | null) => void;
}) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const { data: url } = useCaseCoverUrl(value);

  async function handleFile(file: File) {
    setErr(null);
    setUploading(true);
    try {
      const ext = file.name.split(".").pop()?.toLowerCase() || "bin";
      const path = `testimonials/${crypto.randomUUID()}.${ext}`;
      const { error } = await supabase.storage
        .from("case-covers")
        .upload(path, file, {
          cacheControl: "31536000",
          contentType: file.type || undefined,
          upsert: false,
        });
      if (error) throw error;
      if (value) await supabase.storage.from("case-covers").remove([value]);
      onChange(path);
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      setUploading(false);
    }
  }

  async function clear() {
    if (value) await supabase.storage.from("case-covers").remove([value]);
    onChange(null);
  }

  return (
    <div className="space-y-3">
      <div className="flex items-start gap-4">
        <div className="flex h-24 w-24 shrink-0 items-center justify-center overflow-hidden rounded-full border border-border bg-secondary">
          {url ? (
            <img src={url} alt="Testimonial" className="h-full w-full object-cover" />
          ) : (
            <ImageIcon className="h-5 w-5 text-muted-foreground" />
          )}
        </div>
        <div className="flex flex-col gap-2">
          <button
            type="button"
            onClick={() => inputRef.current?.click()}
            disabled={uploading}
            className="inline-flex items-center gap-2 rounded-md border border-border bg-background px-3 py-2 text-xs font-semibold hover:bg-secondary disabled:opacity-60"
          >
            {uploading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Upload className="h-3.5 w-3.5" />}
            {value ? "Bild ersetzen" : "Bild hochladen"}
          </button>
          {value && (
            <button
              type="button"
              onClick={clear}
              className="inline-flex items-center gap-2 rounded-md border border-destructive/40 px-3 py-2 text-xs font-semibold text-destructive hover:bg-destructive/10"
            >
              <Trash2 className="h-3.5 w-3.5" /> Entfernen
            </button>
          )}
          <p className="text-xs text-muted-foreground">JPG, PNG oder WebP. Quadratisch, min. 400×400 px.</p>
        </div>
      </div>
      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        className="hidden"
        onChange={(e) => {
          const f = e.target.files?.[0];
          if (f) handleFile(f);
          e.target.value = "";
        }}
      />
      {err && <p className="text-xs text-destructive">{err}</p>}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Case-Module: Snippet-Buttons für den Markdown-Editor
// ---------------------------------------------------------------------------

const CASE_MODULE_SNIPPETS: MarkdownEditorSnippet[] = [
  {
    label: "Zwei Spalten",
    title: "Zweispaltiger Textblock (60/40)",
    snippet:
      ':::two-col\nLinke Spalte – Hauptgedanke, Fließtext.\n:::split\nRechte Spalte – Kontext, Rahmenbedingungen.\n:::',
  },
  {
    label: "Text + Bild",
    title: "Zweispaltig Text neben Bild",
    snippet:
      ':::text-image side="right" src="inline/DATEI.jpg" caption="Bildunterschrift"\nAbsatz, der neben dem Bild steht.\n:::',
  },
  {
    label: "Bild groß",
    title: "Vollbreites Bild",
    snippet: ':::image-full src="inline/DATEI.jpg" caption="Bildunterschrift"\n:::',
  },
  {
    label: "Galerie",
    title: "Bildergalerie (2 oder 3 Spalten)",
    snippet:
      ':::gallery cols="3"\ninline/BILD-1.jpg\ninline/BILD-2.jpg\ninline/BILD-3.jpg\n:::',
  },
  {
    label: "Kennzahlen",
    title: "Kennzahlen / Stats",
    snippet:
      ":::stats\n62 % | Wahlergebnis\n14 | Wochen Kampagne\n8 | Ortsteile bespielt\n:::",
  },
  {
    label: "Pull-Quote",
    title: "Hervorgehobenes Zitat im Fließtext",
    snippet: ':::pullquote author="Optional"\nEin Satz, der aus dem Text heraussticht.\n:::',
  },
  {
    label: "Vimeo-Video",
    title: "Vimeo-Video im 16:9-Format einbetten",
    snippet: ':::vimeo url="https://vimeo.com/VIDEO-ID" caption=""\n:::',
  },
  {
    label: "Vimeo-Paar (9:16)",
    title: "Zwei Vimeo-Videos nebeneinander im Hochformat",
    snippet:
      ':::vimeo-pair url1="https://vimeo.com/VIDEO-ID-1" url2="https://vimeo.com/VIDEO-ID-2" caption1="" caption2=""\n:::',
  },
  {
    label: "Audio-Player",
    title: "Audiodatei als Player einbetten",
    snippet: ':::audio src="inline/DATEI.mp3" caption=""\n:::',
  },
  {
    label: "Testimonial",
    title: "Ankerpunkt für das oben gepflegte Testimonial",
    snippet: ":::testimonial:::",
  },
];

// ---------------------------------------------------------------------------
// Vimeo-Vorschau: erkennt Marker und nackte Vimeo-URLs im Markdown und zeigt
// live einen 16:9-Player, damit Redakteure den Link nicht erst speichern müssen.
// ---------------------------------------------------------------------------
